import React from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Button from './Button';
import { useStepBarTarget } from './StepBarSlot';

/**
 * StepBar — the step's forward action, stuck to the bottom of the main
 * column on every wizard step: Back · the step label + a one-line summary of
 * the whole step · the primary action, named for where it goes. Render it as
 * the last child of the step layout. Inside the wizard it is portalled into the
 * StepBarSlot at the foot of the main column, full-bleed and flush; anywhere
 * else it renders in place, `sticky bottom-0`. During a quick wait its
 * own button shows the shared busy state; under an overlay it stays put.
 */
export interface StepBarCta {
  label: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  /** Verb while busy ("Saving…"). */
  loadingLabel?: React.ReactNode;
  /** Defaults to a right arrow; pass `null` for none. */
  icon?: React.ReactNode | null;
}

export interface StepBarProps {
  /** "Step 3 · Context" — rendered small caps above the summary. */
  stepLabel: React.ReactNode;
  /** One line about the whole step; truncated, never wraps. */
  summary: React.ReactNode;
  /** Omit when the viewer has nothing to do here — never render a dead button. */
  cta?: StepBarCta;
  /** Small line under the CTA (why it's disabled, what it costs). */
  ctaNote?: React.ReactNode;
  onBack?: () => void;
  backLabel?: React.ReactNode;
  className?: string;
}

export const StepBar: React.FC<StepBarProps> = ({
  stepLabel,
  summary,
  cta,
  ctaNote,
  onBack,
  backLabel = 'Back',
  className = '',
}) => {
  const target = useStepBarTarget();
  const bar = (
  <div
    className={`sticky bottom-0 z-30 flex min-h-[64px] items-center gap-s5 border-t border-line-strong bg-surface px-[26px] pb-s4 pt-[11px] shadow-stepbar ${className}`}
  >
    {onBack && (
      <Button variant="secondary" size="lg" iconLeft={<ArrowLeft />} onClick={onBack} className="font-medium">
        {backLabel}
      </Button>
    )}
    <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
      <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-ink-muted">{stepLabel}</span>
      <span className="truncate text-body-md text-ink-body">{summary}</span>
    </div>
    {cta && (
      <div className="ml-auto flex shrink-0 flex-col items-end gap-s1">
        <Button
          size="lg"
          onClick={cta.onClick}
          disabled={cta.disabled}
          loading={cta.loading}
          loadingLabel={cta.loadingLabel}
          iconRight={cta.icon === undefined ? <ArrowRight /> : cta.icon ?? undefined}
        >
          {cta.label}
        </Button>
        {ctaNote && <span className="whitespace-nowrap text-meta text-ink-secondary">{ctaNote}</span>}
      </div>
    )}
  </div>
  );
  return target ? createPortal(bar, target) : bar;
};

export default StepBar;
